import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ExternalLink, CheckCircle, XCircle } from "lucide-react";
import { PartnerProfile } from "@/hooks/use-partner-profile";

interface PartnerSectionProps {
  partnerData: PartnerProfile;
  onPartnerFieldChange: (field: keyof PartnerProfile, value: any) => void;
  onViewDetails?: () => void;
}

export const PartnerSection = ({ 
  partnerData, 
  onPartnerFieldChange, 
  onViewDetails 
}: PartnerSectionProps) => {
  const getStatusColor = (status: string) => {
    return status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  // Removed getTierColor as we're removing tier badges

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Partner Information</h2>
        {onViewDetails && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onViewDetails}
            className="gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            View Full Details
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {/* Status Badges */}
        <div className="flex flex-wrap gap-2">
          <Badge className={getStatusColor(partnerData.status)}>
            {partnerData.status}
          </Badge>
          <Badge variant="outline">
            {partnerData.partner_type}
          </Badge>
          <Badge 
            variant={partnerData.status === 'Signed' ? "default" : partnerData.status === 'Not-Signed' ? "destructive" : "secondary"}
            className="gap-1"
          >
            {partnerData.status === 'Signed' ? (
              <CheckCircle className="h-3 w-3" />
            ) : (
              <XCircle className="h-3 w-3" />
            )}
            {partnerData.status}
          </Badge>
        </div>

        {/* Partner-specific fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="partner_type">Partner Type</Label>
            <Select
              value={partnerData.partner_type}
              onValueChange={(value) => onPartnerFieldChange('partner_type', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Affiliate">Affiliate</SelectItem>
                <SelectItem value="Associate">Associate</SelectItem>
                <SelectItem value="Reseller">Reseller</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="territory">Territory</Label>
            <Input
              id="territory"
              value={partnerData.territory || ""}
              onChange={(e) => onPartnerFieldChange('territory', e.target.value)}
              placeholder="Geographic territory"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="specialization">Specialization</Label>
            <Input
              id="specialization"
              value={partnerData.specialization || ""}
              onChange={(e) => onPartnerFieldChange('specialization', e.target.value)}
              placeholder="Area of expertise"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="commission_rate">Commission Rate (%)</Label>
            <Input
              id="commission_rate"
              type="number"
              value={partnerData.commission_rate || ""}
              onChange={(e) => onPartnerFieldChange('commission_rate', parseFloat(e.target.value))}
              placeholder="0.00"
              disabled
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">Contact admin to modify commission rate</p>
          </div>
        </div>

        {/* Performance metrics (read-only) */}
        {(partnerData.deals_won || partnerData.revenue_generated || partnerData.total_earned) && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {partnerData.deals_won || 0}
              </div>
              <div className="text-sm text-muted-foreground">Deals Won</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                £{(partnerData.revenue_generated || 0).toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground">Revenue Generated</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                £{(partnerData.total_earned || 0).toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground">Total Earned</div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};